import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useBrowseTrailStore } from "@web/stores/browseTrail.ts";
import { useDetailStore } from "@web/stores/detail.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { formatSheetParam, parseSheetParam } from "./sheetUrl.ts";

/**
 * Keep `?sheet=type:id` in sync with the open entity (replace — no history spam).
 * Hydrate the sheet from the URL on first load / when the param appears.
 */
export function useSheetUrlSync() {
  const [params, setParams] = useSearchParams();
  const target = useDetailStore((s) => s.target);
  const sheetOpen = useUiStore((s) => s.sheetOpen);
  const hydrated = useRef(false);

  // Hydrate once from URL (reload / shared link).
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const parsed = parseSheetParam(params.get("sheet"));
    if (!parsed) return;
    const current = useDetailStore.getState().target;
    if (current?.type === parsed.type && current.id === parsed.id) {
      useUiStore.getState().setSheetOpen(true);
      return;
    }
    useBrowseTrailStore.getState().resetTo({
      type: parsed.type,
      id: parsed.id,
      label: parsed.id,
    });
    useDetailStore.getState().setTarget(parsed);
    useUiStore.getState().setSheetOpen(true);
  }, [params]);

  // Reflect current sheet entity into the URL (replace only).
  useEffect(() => {
    const desired =
      sheetOpen && target ? formatSheetParam(target.type, target.id) : null;
    const current = params.get("sheet");
    if (desired === current) return;
    if (desired === null && current === null) return;

    const next = new URLSearchParams(params);
    if (desired) next.set("sheet", desired);
    else next.delete("sheet");
    setParams(next, { replace: true });
  }, [sheetOpen, target, params, setParams]);
}
